import React from 'react';
import { ArrowLeft, Download, Calendar, Users, Building2, TrendingDown, Clock, Phone, CheckCircle, AlertTriangle } from 'lucide-react';

interface Employee {
  id: string;
  name: string;
  role: string;
  department: string;
  todayUsage: number;
  weeklyAverage: number;
  goalMinutes: number;
  status: 'excellent' | 'good' | 'warning' | 'critical';
  lastActive: string;
  trend: 'up' | 'down' | 'stable';
}

interface ReportPageProps {
  onBack: () => void;
}

export default function ReportPage({ onBack }: ReportPageProps) {
  const employees: Employee[] = [
    {
      id: '1',
      name: 'Sarah Johnson',
      role: 'Delivery Driver',
      department: 'Logistics',
      todayUsage: 15,
      weeklyAverage: 22,
      goalMinutes: 30,
      status: 'excellent',
      lastActive: '2 hours ago',
      trend: 'down'
    },
    {
      id: '2',
      name: 'Mike Chen',
      role: 'Warehouse Supervisor',
      department: 'Operations',
      todayUsage: 45,
      weeklyAverage: 38,
      goalMinutes: 30,
      status: 'warning',
      lastActive: '30 minutes ago',
      trend: 'up'
    },
    {
      id: '3',
      name: 'Emily Rodriguez',
      role: 'Safety Coordinator',
      department: 'Safety',
      todayUsage: 12,
      weeklyAverage: 18,
      goalMinutes: 30,
      status: 'excellent',
      lastActive: '1 hour ago',
      trend: 'down'
    },
    {
      id: '4',
      name: 'David Thompson',
      role: 'Delivery Driver',
      department: 'Logistics',
      todayUsage: 65,
      weeklyAverage: 72,
      goalMinutes: 30,
      status: 'critical',
      lastActive: '15 minutes ago',
      trend: 'down'
    },
    {
      id: '5',
      name: 'Lisa Park',
      role: 'Fleet Manager',
      department: 'Operations',
      todayUsage: 28,
      weeklyAverage: 25,
      goalMinutes: 30,
      status: 'good',
      lastActive: '45 minutes ago',
      trend: 'stable'
    },
    {
      id: '6',
      name: 'James Wilson',
      role: 'Delivery Driver',
      department: 'Logistics',
      todayUsage: 8,
      weeklyAverage: 14,
      goalMinutes: 30,
      status: 'excellent',
      lastActive: '3 hours ago',
      trend: 'down'
    }
  ];

  const totalEmployees = employees.length;
  const averageUsage = Math.round(employees.reduce((sum, emp) => sum + emp.todayUsage, 0) / totalEmployees);
  const complianceRate = Math.round((employees.filter(emp => emp.todayUsage <= emp.goalMinutes).length / totalEmployees) * 100);
  const criticalAlerts = employees.filter(emp => emp.status === 'critical').length;

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const handlePrint = () => {
    window.print();
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'excellent': return 'Excellent';
      case 'good': return 'Good';
      case 'warning': return 'Warning';
      case 'critical': return 'Critical';
      default: return 'Unknown';
    }
  };

  const getTrendText = (trend: string) => {
    switch (trend) {
      case 'up': return '↗ Increasing';
      case 'down': return '↘ Decreasing';
      default: return '→ Stable';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Hidden in print */}
      <header className="bg-white shadow-sm border-b border-gray-100 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-12 w-auto mt-1" />
              <span className="text-2xl font-bold text-gray-900">Spaces</span>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={handlePrint}
                className="flex items-center space-x-2 bg-cyan-500 text-white px-4 py-2 rounded-2xl hover:bg-cyan-600 transition-colors"
              >
                <Download className="h-5 w-5" />
                <span>Download PDF</span>
              </button>
              <button 
                onClick={onBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
                <span>Back to Dashboard</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Report Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:px-0 print:py-4">
        {/* Report Header */}
        <div className="mb-8 print:mb-6">
          <div className="flex items-center justify-between mb-4 print:mb-2">
            <div className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-8 w-auto print:h-6" />
              <h1 className="text-3xl font-bold text-gray-900 print:text-2xl">Employee Phone Usage Report</h1>
            </div>
            <div className="text-right text-gray-600">
              <div className="flex items-center space-x-2 mb-1">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">Generated: {currentDate}</span>
              </div>
              <div className="text-sm">Report Period: Last 7 Days</div>
            </div>
          </div>
        </div>

        {/* Employee Data Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden print:border-gray-400">
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 print:bg-gray-100 print:px-3 print:py-2">
            <h2 className="text-lg font-semibold text-gray-900 print:text-base">Employee Phone Usage Data</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 print:bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-3 print:py-2 print:text-xs">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-3 print:py-2 print:text-xs">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-3 print:py-2 print:text-xs">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-3 print:py-2 print:text-xs">
                    Today's Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-3 print:py-2 print:text-xs">
                    Weekly Avg
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-3 print:py-2 print:text-xs">
                    Goal (min)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-3 print:py-2 print:text-xs">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-3 print:py-2 print:text-xs">
                    Trend
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider print:px-3 print:py-2 print:text-xs">
                    Last Active
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employees.map((employee, index) => (
                  <tr key={employee.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap print:px-3 print:py-2">
                      <div className="font-medium text-gray-900 print:text-sm">{employee.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 print:px-3 print:py-2 print:text-xs">
                      {employee.role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 print:px-3 print:py-2 print:text-xs">
                      {employee.department}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap print:px-3 print:py-2">
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 text-gray-400 mr-2 print:h-3 print:w-3" />
                        <span className="font-medium text-gray-900 print:text-sm">{employee.todayUsage}m</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 print:px-3 print:py-2 print:text-xs">
                      {employee.weeklyAverage}m
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 print:px-3 print:py-2 print:text-xs">
                      {employee.goalMinutes}m
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap print:px-3 print:py-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full print:px-1 print:py-0 print:text-xs ${
                        employee.status === 'excellent' ? 'bg-green-100 text-green-800' :
                        employee.status === 'good' ? 'bg-blue-100 text-blue-800' :
                        employee.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {getStatusText(employee.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 print:px-3 print:py-2 print:text-xs">
                      {getTrendText(employee.trend)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 print:px-3 print:py-2 print:text-xs">
                      {employee.lastActive}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Report Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-500 print:mt-4 print:pt-3">
          <div className="flex items-center justify-center space-x-4 print:space-x-2">
            <div className="flex items-center space-x-1">
              <Building2 className="h-4 w-4 print:h-3 print:w-3" />
              <span>Nix Spaces - Employee Monitoring System</span>
            </div>
            <span>•</span>
            <span>Confidential Report</span>
            <span>•</span>
            <span>Generated on {currentDate}</span>
          </div>
        </div>
      </div>
    </div>
  );
}